import type { SourcePostFilters } from './types.ts';

/**
 * WSTĄŻKA NA ZDJĘCIU KARTY — kolejność, jedna dla strony wyników i e-maili z alertami (plan §1.68,
 * docs/AlertEmails.md §3.1). Tu, w czystym module zgodnym z Deno, bo `send-filter-alerts` musi
 * wybrać TĘ SAMĄ wstążkę co karta, a `src/lib/wyroznikiOferty.ts` (pełny katalog wyróżników) ciągnie
 * zależności, których Deno nie rozwiąże. `wyroznikiOferty.ts` importuje stąd i eksportuje dalej.
 *
 * Napisu tu nie ma: strona bierze klucz i18n, e-mail własny napis („Flat Hero” dwoma słowami).
 */

/** Wyróżniki, które mogą stanąć na wstążce. */
export type IdWstazki = 'zweryfikowany' | 'wlasciciel' | 'tanio' | 'zwierzeta';

/** Najmniej, czego potrzebuje wybór wstążki: id i to, czy najemca ma tę cechę w filtrach. */
export interface KandydatWstazki {
  id: string;
  pasuje: boolean;
}

/** Wstążka „cechowa”: tylko te, w tej kolejności (taniej > zwierzęta). */
export const DOZWOLONE_NA_WSTAZCE = ['tanio', 'zwierzeta'] as const;

/**
 * Pierwszy DOZWOLONY (taniej, potem zwierzęta), którego najemca NIE ma w filtrach — cecha z filtra
 * jest w pastylkach z ✓ (wariant 2), a na każdej karcie listy byłaby tą samą wstążką co w filtrze.
 */
export function wyroznikWstazki<W extends KandydatWstazki>(lista: readonly W[]): W | null {
  for (const id of DOZWOLONE_NA_WSTAZCE) {
    const w = lista.find((x) => x.id === id && !x.pasuje);
    if (w) return w;
  }
  return null;
}

/**
 * Wstążka KARTY: JEDNA na zdjęciu, zaufanie pierwsze (Piotr 30.09): zweryfikowany → właściciel na
 * Flat Hero → taniej → zwierzęta.
 */
export function wyroznikWstazkiKarty<W extends KandydatWstazki>(lista: readonly W[]): W | null {
  return lista.find((x) => x.id === 'zweryfikowany' || x.id === 'wlasciciel') ?? wyroznikWstazki(lista);
}

/** Pola oferty, z których bierze się kandydatów na wstążkę (kolumny `SERP_COLUMNS`). */
export interface OfertaDoWstazki {
  owner_confirmed?: boolean | null;
  owner_present?: boolean | null;
  taniej_o_procent?: number | null;
  extracted_extras?: unknown;
}

/**
 * Wstążka oferty w kontekście zestawu — to, co karta wyliczy z pełnego katalogu
 * (`wyroznikWstazkiKarty(wyroznikiOferty(o, { filtry }))`), policzone z samych czterech
 * kandydatów. Zgodność z kartą pilnuje `src/lib/wyroznikiOferty.test.ts`.
 */
export function wstazkaOferty(o: OfertaDoWstazki, filtry: SourcePostFilters = {}): IdWstazki | null {
  const e = (o.extracted_extras as Record<string, unknown> | null) ?? {};
  const kandydaci: Array<{ id: IdWstazki; pasuje: boolean }> = [];
  if (o.owner_confirmed === true) kandydaci.push({ id: 'zweryfikowany', pasuje: false });
  else if (o.owner_present === true) kandydaci.push({ id: 'wlasciciel', pasuje: false });
  if (o.taniej_o_procent != null && o.taniej_o_procent > 0) kandydaci.push({ id: 'tanio', pasuje: false });
  // Ta sama definicja co `ma('zwierzeta')` i `stanWarunku('zwierzeta')`: „do uzgodnienia” to nie wyróżnik.
  if (e.pets_allowed === true && e.pet_terms_negotiable !== true) {
    kandydaci.push({ id: 'zwierzeta', pasuje: !!filtry.zeZwierzeciem });
  }
  return wyroznikWstazkiKarty(kandydaci)?.id ?? null;
}
